// Homepage delegates to the catch-all dispatcher's LOADER so `/` runs through
// the same Builder-override decision tree as every other path (SCAPI URL
// Mapping is skipped for `/` inside the dispatcher; the homepage's Builder
// page wins via the override branch).
//
// COM-188: the COMPONENT deliberately does NOT re-export the catch-all's
// default. `routes/$.tsx`'s component statically imports the PDP and PLP page
// components for its `pdp` / `plp` payload kinds, which welds product-details,
// product-buy-form, search and both carousel libraries (~360KB) into any
// client chunk that imports it. `/` can never produce those payloads — its
// dispatch is either the Builder override (`type: "builder"`) or a thrown
// SFRA-proxy/redirect — so the homepage renders the Builder view directly and
// keeps that graph out of the homepage's critical path. The loader/headers
// re-exports below are server-only and stripped from the client build.
import { useLoaderData } from "react-router";

import { defineShell } from "~/hec/ppr/define-shell";

import { BuilderPageView, builderEntryMeta } from "./builder-page-view";

export { loader, headers } from "./$";
export { dispatcherShouldRevalidate as shouldRevalidate } from "./dispatcher-should-revalidate";
export { PDPErrorBoundary as ErrorBoundary } from "~/components/error-boundries/pdp-error-boundary";

// COM-482: the homepage document is served from a cached guest shell
// (docs/ppr-shell.md). The Builder page tree itself is not in loader data
// (flight diet) — it is SSRed from the nonce-keyed stash and digested
// separately as `routes/index:ssr-page`.
export const shell = defineShell({
  /** This route's loader fields that render OUTSIDE holes (fingerprinted). */
  fingerprint: [
    "type",
    "pageType",
    "hideLayoutHeader",
    "hideLayoutFooter",
    "data",
  ],
  /** `data.ssrKey` is the CSP nonce that keys the SSR page stash (`routes/$.tsx`). */
  perRequestKeys: { data: ["ssrKey"] },
});

// Mirrors the catch-all's meta for the only payload kind `/` can render.
export const meta = ({
  loaderData,
}: {
  loaderData?: { type?: string; data?: { page?: unknown } };
}) => {
  if (loaderData?.type === "builder") {
    return builderEntryMeta(
      loaderData.data?.page as Parameters<typeof builderEntryMeta>[0],
    );
  }
  return [];
};

export default function HomePage() {
  const loaderData = useLoaderData<{
    type: string;
    data: React.ComponentProps<typeof BuilderPageView>["payload"];
  }>();
  if (loaderData.type !== "builder") {
    // Unreachable by construction (see header comment). Loud, not silent:
    // if the dispatcher ever grows a non-builder RENDER outcome for `/`,
    // this must become a real render path, not a blank page.
    console.error(
      "[routes/index] non-builder payload reached the homepage component",
      { type: loaderData.type },
    );
    return null;
  }
  return <BuilderPageView payload={loaderData.data} />;
}
